/**
 * First-login (and self-service) password rotation.
 * Reached when the staff login response carries must_change_password=true,
 * or from any account menu link.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, LogOut, Save } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useGate } from "@/context/GateContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Input } from "@/components/ui/input";

const TEMP_PASSWORD = "Canales2026";

export default function ChangePassword() {
  const { lang } = useLang();
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth() || {};
  const gate = useGate() || {};

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const mustChange = !!user?.must_change_password;

  // If this is a first-time login (must_change_password=true), pre-fill the
  // temp password so vendors don't confuse the placeholder with an input.
  // Users had been submitting with an empty `current` field because the
  // greyed placeholder "Canales2026" looked like an already-typed value.
  useEffect(() => {
    if (mustChange && !current) {
      setCurrent(TEMP_PASSWORD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustChange]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(
        lang === "es"
          ? "La nueva contraseña debe tener al menos 6 caracteres."
          : "New password must be at least 6 characters."
      );
      return;
    }
    if (next !== confirm) {
      toast.error(lang === "es" ? "Las contraseñas no coinciden." : "Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/staff/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(lang === "es" ? "Contraseña actualizada." : "Password updated.");
      if (refresh) await refresh();
      if (gate?.check) await gate.check();
      // Route to the appropriate landing based on role.
      const role = user?.role;
      if (role === "manager") nav("/manager/team", { replace: true });
      else if (role === "seller") nav("/magazine", { replace: true });
      else nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 bg-halftone"
      data-testid="change-password-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md brutal p-8 bg-white space-y-6"
      >
        <div className="text-center">
          <CanalesLogo width={160} className="mx-auto mb-2" />
          <div className="inline-flex items-center gap-2 bg-canales-yellow border-2 border-black rounded-full px-3 py-1 font-display font-bold text-xs uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" />
            {mustChange
              ? lang === "es"
                ? "Primer inicio de sesión"
                : "First-time login"
              : lang === "es"
              ? "Cambiar contraseña"
              : "Change password"}
          </div>
          <h1 className="mt-3 font-display font-black text-3xl">
            {mustChange
              ? lang === "es"
                ? "Crea tu contraseña"
                : "Create your password"
              : lang === "es"
              ? "Nueva contraseña"
              : "New password"}
          </h1>
          <p className="mt-1 text-sm font-body text-neutral-600">
            {mustChange
              ? lang === "es"
                ? `Hola ${user?.name || ""}, por seguridad crea una contraseña personal antes de continuar.`
                : `Hi ${user?.name || ""}, for security please pick your own password to continue.`
              : lang === "es"
              ? "Escribe tu contraseña actual y la nueva."
              : "Enter your current and new password."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {mustChange
                ? lang === "es"
                  ? "Contraseña temporal (ya rellenada)"
                  : "Temporary password (pre-filled)"
                : lang === "es"
                ? "Contraseña actual"
                : "Current password"}
            </span>
            <div className="mt-1 relative">
              <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
              <Input
                data-testid="cp-current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="brutal-sm pl-10 py-6 text-lg"
              />
            </div>
            {mustChange && (
              <span className="mt-1 block text-[11px] font-body text-neutral-500">
                {lang === "es"
                  ? "Este campo ya lleva tu contraseña temporal. Solo escribe la nueva abajo."
                  : "This field already has your temporary password. Just type the new one below."}
              </span>
            )}
          </label>

          <label className="block">
            <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Nueva contraseña" : "New password"}
            </span>
            <div className="mt-1 relative">
              <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
              <Input
                data-testid="cp-new"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                placeholder={lang === "es" ? "Mínimo 6 caracteres" : "min 6 chars"}
                className="brutal-sm pl-10 py-6 text-lg"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Confirmar contraseña" : "Confirm password"}
            </span>
            <div className="mt-1 relative">
              <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-neutral-500" />
              <Input
                data-testid="cp-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••"
                className="brutal-sm pl-10 py-6 text-lg"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={busy || !current || !next || !confirm}
            data-testid="cp-submit"
            className="w-full brutal-btn bg-canales-blue text-white px-6 py-4 text-lg flex items-center justify-center gap-2 font-display font-black disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {lang === "es" ? "Guardar contraseña" : "Save password"}
          </button>
        </form>

        {!mustChange && (
          <div className="border-t-2 border-dashed border-neutral-300 pt-4 text-center text-xs font-body text-neutral-600">
            <Link
              to="/"
              className="font-display font-bold text-canales-red underline"
            >
              {lang === "es" ? "Volver al inicio" : "Back home"}
            </Link>
          </div>
        )}
        {mustChange && (
          <button
            type="button"
            onClick={async () => {
              if (logout) await logout();
              nav("/login", { replace: true });
            }}
            className="w-full text-xs text-neutral-500 font-body hover:underline flex items-center justify-center gap-1"
          >
            <LogOut className="w-3 h-3" />
            {lang === "es" ? "Salir sin cambiar" : "Sign out without changing"}
          </button>
        )}
      </motion.div>
    </div>
  );
}
